import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { userRequest, getDomainUrl } from "../../utils/requestMethods";
import { Header, ButtonLoading, FormInput, BackButton } from "../../components";
import { useStateContext } from "../../contexts/ContextProvider";
import {
  MdAdd,
  MdDelete,
  MdDragIndicator,
  MdKeyboardArrowUp,
  MdKeyboardArrowDown,
} from "react-icons/md";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import {
  EDUCATION_LEVELS,
  getEducationLevelOptions,
  getEducationLevelLabel,
  getDefaultSectionsForLevel,
} from "../../utils/enrollmentUtils";

// Field type enum
const EEnrolFormFieldType = {
  text: "text",
  number: "number",
  email: "email",
  phone: "phone",
  date: "date",
  select: "select",
  checkbox: "checkbox",
  textarea: "textarea",
  file: "file",
  radio: "radio",
};

// Field types that require options
const FIELD_TYPES_WITH_OPTIONS = ["select", "radio", "checkbox"];

const fieldTypes = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "date", label: "Date" },
  { value: "select", label: "Select" },
  { value: "checkbox", label: "Checkbox" },
  { value: "textarea", label: "Text Area" },
  { value: "file", label: "File Upload" },
  { value: "radio", label: "Radio" },
];

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Form title is required"),
  description: Yup.string().required("Form description is required"),
  level_of_education: Yup.string().required("Education level is required"),
  is_active: Yup.boolean(),
  sections: Yup.array().of(
    Yup.object().shape({
      title: Yup.string().required("Section title is required"),
      description: Yup.string().required("Section description is required"),
      order: Yup.number().required("Section order is required"),
      is_active: Yup.boolean(),
      fields: Yup.array().of(
        Yup.object().shape({
          name: Yup.string().required("Field name is required"),
          label: Yup.string().required("Field label is required"),
          type: Yup.string()
            .required("Field type is required")
            .oneOf(Object.values(EEnrolFormFieldType)),
          required: Yup.boolean(),
          order: Yup.number().required("Field order is required"),
          is_active: Yup.boolean(),
        }),
      ),
    }),
  ),
});

// Required fields that cannot be edited (name and type)
const requiredEnrolTemplateFields = [
  "child_first_name",
  "child_last_name",
  "gender",
  "class",
  "previous_school",
  "dob",
  "nationality",
  "religion",
  "parent1_first_name",
  "parent1_last_name",
  "parent1_relationship",
  "parent1_occupation",
  "parent1_contact",
  "parent1_email",
  "parent1_address_line",
  "parent1_city",
  "parent1_state",
  "parent2_first_name",
  "parent2_last_name",
  "parent2_relationship",
  "parent2_occupation",
  "parent2_contact",
  "parent2_email",
  "parent2_address_line",
  "parent2_city",
  "parent2_state",
  "consent",
];

// Expected defaults for system-required fields (name-keyed)
const defaultRequiredFieldConfigs = {
  child_first_name: { label: "Child's First Name", type: "text", required: true },
  child_last_name: { label: "Child's Last Name", type: "text", required: true },
  dob: { label: "Date of Birth", type: "date", required: true },
  gender: { label: "Gender", type: "select", required: true, options: ["Male", "Female"] },
  nationality: { label: "Nationality", type: "text", required: true },
  religion: { label: "Religion", type: "text", required: true },
  class: { label: "Class", type: "text", required: true },
  parent1_first_name: { label: "Parent/Guardian 1 First Name", type: "text", required: true },
  parent1_last_name: { label: "Parent/Guardian 1 Last Name", type: "text", required: true },
  parent1_relationship: { label: "Relationship to Child", type: "text", required: true },
  parent1_occupation: { label: "Occupation", type: "text", required: true },
  parent1_contact: { label: "Contact Number", type: "phone", required: true },
  parent1_email: { label: "Email", type: "email", required: false },
  parent1_address_line: { label: "Address Line 1", type: "text", required: true },
  parent1_city: { label: "City", type: "text", required: true },
  parent1_state: { label: "State", type: "text", required: true },
  parent2_first_name: { label: "Parent/Guardian 2 First Name", type: "text", required: true },
  parent2_last_name: { label: "Parent/Guardian 2 Last Name", type: "text", required: true },
  parent2_relationship: { label: "Relationship to Child", type: "text", required: true },
  parent2_occupation: { label: "Occupation", type: "text", required: true },
  parent2_contact: { label: "Contact Number", type: "phone", required: true },
  parent2_email: { label: "Email", type: "email", required: false },
  parent2_address_line: { label: "Address Line 1", type: "text", required: true },
  parent2_city: { label: "City", type: "text", required: true },
  parent2_state: { label: "State", type: "text", required: true },
  emergency_contact_person: { label: "Emergency Contact Person", type: "text", required: true },
  consent: { label: "consent", type: "radio", required: true },
};

// Normalize sections/fields to enforce defaults for required fields
const normalizeSections = (sections) => {
  if (!Array.isArray(sections)) return [];
  return sections.map((section, sectionIdx) => {
    const normalizedFields = (section.fields || []).map((field, fieldIdx) => {
      if (!requiredEnrolTemplateFields.includes(field.name)) {
        // custom field: return as-is, ensure order preserved
        return { ...field, order: field.order ?? fieldIdx + 1 };
      }
      const defaults = defaultRequiredFieldConfigs[field.name] || {};
      const base = {
        ...field,
        label: defaults.label ?? field.label,
        type: defaults.type ?? field.type,
        required: typeof defaults.required === "boolean" ? defaults.required : !!field.required,
        order: field.order ?? fieldIdx + 1,
        is_active: typeof field.is_active === "boolean" ? field.is_active : true,
      };

      // Handle options: only keep for fields that need them; set defaults if provided
      if (["select", "checkbox", "radio"].includes(base.type)) {
        const defaultOptions = Array.isArray(defaults.options)
          ? defaults.options
          : Array.isArray(field.options)
            ? field.options
            : [];
        return { ...base, options: defaultOptions };
      }
      const { options, ...withoutOptions } = base;
      return withoutOptions;
    });

    // Re-number orders within section
    normalizedFields.forEach((f, idx) => {
      f.order = idx + 1;
    });

    return {
      ...section,
      order: section.order ?? sectionIdx + 1,
      is_active: typeof section.is_active === "boolean" ? section.is_active : true,
      fields: normalizedFields,
    };
  });
};

const generateSlug = (label) => {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "") // Remove special characters
    .trim()
    .replace(/\s+/g, "_"); // Replace spaces with underscores
};

const UpdateEnrollmentForm = ({ history }) => {
  const { school } = useStateContext();
  const { templateId, levelOfEducation } = useParams();
  const schoolId = school?._id;
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fetchedTemplateId, setFetchedTemplateId] = useState(null);

  const fieldTypesRequiringOptions = ["select", "checkbox"];

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      level_of_education: EDUCATION_LEVELS.PRIMARY,
      is_active: true,
      sections: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      const currentTemplateId = templateId || fetchedTemplateId;
      if (!currentTemplateId) {
        setError("Template ID is missing");
        return;
      }

      setLoading(true);
      try {
        //console.log('Form submission started:', { values, currentTemplateId, schoolId });

        // Enforce defaults for required fields before saving
        const normalizedSections = normalizeSections(values.sections);

        // Clean up the values to match API requirements
        const cleanedValues = {
          title: values.title,
          description: values.description,
          level_of_education: values.level_of_education,
          is_active: values.is_active,
          sections: normalizedSections.map((section) => {
            // Remove _id from section and clean fields
            const { _id: sectionId, fields, ...cleanSection } = section;
            return {
              ...cleanSection,
              fields: fields.map((field) => {
                const { _id: fieldId, ...cleanField } = field;

                // Only include options for fields that require them (excluding radio for consent)
                if (fieldTypesRequiringOptions.includes(field.type)) {
                  return {
                    ...cleanField,
                    options: (field.options || []).filter((opt) => opt.trim()),
                  };
                }

                // Remove options for fields that don't need them
                const { options, ...fieldWithoutOptions } = cleanField;
                return fieldWithoutOptions;
              }),
            };
          }),
        };

        await userRequest.patch(
          `/schools/${schoolId}/enrol-form-template/${currentTemplateId}`,
          cleanedValues,
        );
        setSuccess("Enrollment form template updated successfully");
        setTimeout(() => {
          setSuccess("");
          //history.push('/enrollment/forms');
        }, 3000);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to update enrollment form template");
        setTimeout(() => {
          setError("");
        }, 3000);
      } finally {
        setLoading(false);
      }
    },
  });

  /* Fetch existing template data
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        // Try to get template by ID first (if API supports it)
        let template;
        try {
          const response = await userRequest.get(`/schools/${schoolId}/enrol-form-template/${templateId}`);
          template = response.data;
        } catch (idError) {
          // If that fails, we might need to get all templates and find the one with matching ID
          console.log('Failed to fetch by ID, trying alternative approach');
          throw new Error('Template not found');
        }
      } catch (err) {
        setError(err.response?.data || "Failed to fetch enrollment form template");
        setTimeout(() => {
          setError("");
        }, 3000);
      } finally {
        setFetchLoading(false);
      }
    };

    if (schoolId && templateId) {
      fetchTemplate();
    }
  }, [schoolId, templateId, levelOfEducation]);*/

  // Fetch existing template data
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await userRequest.get(
          `/schools/${school.slug}/${levelOfEducation}/enrol-form-template`,
        );
        const template = response.data;

        // Set templateId for form submission
        if (template._id) {
          setFetchedTemplateId(template._id);
        }

        // Normalize fetched template to enforce defaults for required fields
        const normalized = normalizeSections(template.sections || []);

        // Update formik values with normalized data
        formik.setValues({
          title: template.title || "",
          description: template.description || "",
          level_of_education: template.level_of_education || EDUCATION_LEVELS.PRIMARY,
          is_active: template.is_active ?? true,
          sections: normalized,
        });
      } catch (err) {
        setError(err.response?.data || "Failed to fetch enrollment form template");
        setTimeout(() => {
          setError("");
        }, 3000);
      } finally {
        setFetchLoading(false);
      }
    };

    if (school?.slug && levelOfEducation) {
      fetchTemplate();
    }
  }, [school?.slug, levelOfEducation]);

  const handleFieldLabelChange = (sectionIndex, fieldIndex, value) => {
    const sections = [...formik.values.sections];
    const fields = sections[sectionIndex].fields;
    const field = fields[fieldIndex];

    // Prevent modification of required fields
    if (requiredEnrolTemplateFields.includes(field.name)) {
      return;
    }

    fields[fieldIndex] = {
      ...field,
      label: value,
      name: generateSlug(value),
    };
    formik.setFieldValue("sections", sections);
  };

  const handleFieldTypeChange = (sectionIndex, fieldIndex, value) => {
    const sections = [...formik.values.sections];
    const fields = sections[sectionIndex].fields;
    const field = fields[fieldIndex];

    // Prevent modification of required fields
    if (requiredEnrolTemplateFields.includes(field.name)) {
      return;
    }

    fields[fieldIndex] = {
      ...field,
      type: value,
    };
    formik.setFieldValue("sections", sections);
  };

  const addNewSection = () => {
    formik.setFieldValue("sections", [
      ...formik.values.sections,
      {
        title: "",
        description: "",
        order: formik.values.sections.length + 1,
        is_active: true,
        fields: [],
      },
    ]);
  };

  const removeSection = (sectionIndex) => {
    const sections = formik.values.sections.filter((_, idx) => idx !== sectionIndex);
    sections.forEach((section, idx) => {
      section.order = idx + 1;
    });
    formik.setFieldValue("sections", sections);
  };

  const addNewField = (sectionIndex) => {
    const sections = [...formik.values.sections];
    const newField = {
      name: "",
      label: "",
      type: "text",
      required: false,
      order: sections[sectionIndex].fields.length + 1,
      is_active: true,
    };
    sections[sectionIndex].fields.push(newField);
    formik.setFieldValue("sections", sections);
  };

  const removeField = (sectionIndex, fieldIndex) => {
    const sections = [...formik.values.sections];
    const field = sections[sectionIndex].fields[fieldIndex];

    // Prevent deletion of required fields
    if (requiredEnrolTemplateFields.includes(field.name)) {
      return;
    }

    sections[sectionIndex].fields = sections[sectionIndex].fields.filter(
      (_, idx) => idx !== fieldIndex,
    );
    sections[sectionIndex].fields.forEach((field, idx) => {
      field.order = idx + 1;
    });
    formik.setFieldValue("sections", sections);
  };

  const handleEducationLevelChange = (levelOfEducation) => {
    formik.setFieldValue("level_of_education", levelOfEducation);
    // Optionally update sections with defaults for the selected education level
    // formik.setFieldValue('sections', getDefaultSectionsForLevel(levelOfEducation));
  };

  // Field reordering functions
  const moveFieldUp = (sectionIndex, fieldIndex) => {
    if (fieldIndex === 0) return; // Already at the top

    const sections = [...formik.values.sections];
    const fields = [...sections[sectionIndex].fields];

    // Swap with the field above
    [fields[fieldIndex - 1], fields[fieldIndex]] = [fields[fieldIndex], fields[fieldIndex - 1]];

    // Update order values
    fields.forEach((field, idx) => {
      field.order = idx + 1;
    });

    sections[sectionIndex].fields = fields;
    formik.setFieldValue("sections", sections);
  };

  const moveFieldDown = (sectionIndex, fieldIndex) => {
    const sections = [...formik.values.sections];
    const fields = [...sections[sectionIndex].fields];

    if (fieldIndex === fields.length - 1) return; // Already at the bottom

    // Swap with the field below
    [fields[fieldIndex], fields[fieldIndex + 1]] = [fields[fieldIndex + 1], fields[fieldIndex]];

    // Update order values
    fields.forEach((field, idx) => {
      field.order = idx + 1;
    });

    sections[sectionIndex].fields = fields;
    formik.setFieldValue("sections", sections);
  };

  // Section reordering functions
  const moveSectionUp = (sectionIndex) => {
    if (sectionIndex === 0) return; // Already at the top

    const sections = [...formik.values.sections];

    // Swap with the section above
    [sections[sectionIndex - 1], sections[sectionIndex]] = [
      sections[sectionIndex],
      sections[sectionIndex - 1],
    ];

    // Update order values
    sections.forEach((section, idx) => {
      section.order = idx + 1;
    });

    formik.setFieldValue("sections", sections);
  };

  const moveSectionDown = (sectionIndex) => {
    const sections = [...formik.values.sections];

    if (sectionIndex === sections.length - 1) return; // Already at the bottom

    // Swap with the section below
    [sections[sectionIndex], sections[sectionIndex + 1]] = [
      sections[sectionIndex + 1],
      sections[sectionIndex],
    ];

    // Update order values
    sections.forEach((section, idx) => {
      section.order = idx + 1;
    });

    formik.setFieldValue("sections", sections);
  };

  const handleShareLink = () => {
    if (!school?.slug) {
      toast.error("School information not available");
      return;
    }

    const formUrl = `${getDomainUrl()}/${school.slug}/admission/${formik.values.level_of_education}`;

    // Copy to clipboard
    navigator.clipboard
      .writeText(formUrl)
      .then(() => {
        toast.success("Form link copied to clipboard!");
      })
      .catch(() => {
        toast.error("Failed to copy link");
      });
  };

  if (fetchLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const renderMessage = () => {
    if (success)
      return (
        <div className="fixed top-5 right-5 bg-green-500 text-white p-3 rounded-md shadow-md z-50">
          {success}
        </div>
      );
    if (error)
      return (
        <div className="fixed top-5 right-5 bg-red-500 text-white p-3 rounded-md shadow-md z-50">
          {error}
        </div>
      );
    return null;
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
      {renderMessage()}
      <div className="flex justify-between items-center">
        <BackButton history={history} />
        <button
          onClick={handleShareLink}
          className="inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-indigo-800 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Share form link
        </button>
      </div>
      <Header category="Enrollment" title="Update Enrollment Form" />

      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
        <div>
          <label htmlFor="is_active" className="block text-sm font-medium text-gray-900">
            Admissions Open
          </label>
          <p className="text-xs text-gray-500 mt-1">
            {formik.values.is_active
              ? "Parents can currently submit applications for this class."
              : "Admissions are closed — parents cannot submit applications for this class."}
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={formik.values.is_active}
            onChange={formik.handleChange}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-600 peer-focus:outline-none after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
        </label>
      </div>

      {/* Information about field types */}
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <h3 className="text-sm font-medium text-blue-800">Field Types Information</h3>
            <div className="mt-2 text-sm text-blue-700">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Required Field
                  </span>
                  <span>System fields that cannot be modified</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Custom Field
                  </span>
                  <span>Fields you can add, edit, or remove</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning d-flex align-items-center" role="alert">
          <i className="fal fa-lock pr-10"></i>
          <div>{error}</div>
        </div>
      )}
      {success && (
        <div className="alert alert-success d-flex align-items-center" role="alert">
          <i className="fal fa-check pr-10"></i>
          <div>{success}</div>
        </div>
      )}

      <form onSubmit={formik.handleSubmit} className="mt-4">
        <div className="grid grid-cols-1 gap-6">
          <FormInput
            label="Form Title"
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter form title"
            required
          />
          <div>
            <label htmlFor="description" className="block mb-2 text-sm font-medium text-gray-900">
              Form Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter form description"
              rows="3"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
              required
            />
          </div>
          <div>
            <label
              htmlFor="level_of_education"
              className="block mb-2 text-sm font-medium text-gray-900"
            >
              Education Level <span className="text-red-500">*</span>
            </label>
            <select
              id="level_of_education"
              name="level_of_education"
              value={formik.values.level_of_education}
              onChange={(e) => handleEducationLevelChange(e.target.value)}
              onBlur={formik.handleBlur}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
              required
            >
              {getEducationLevelOptions().map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {formik.touched.level_of_education && formik.errors.level_of_education && (
              <div className="text-red-500 text-sm mt-1">{formik.errors.level_of_education}</div>
            )}
          </div>

          {formik.values.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mt-6 p-6 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <MdDragIndicator className="text-gray-400 mr-2" />
                  <h3 className="text-lg font-semibold">Section {sectionIndex + 1}</h3>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Section reorder buttons */}
                  <div className="flex items-center space-x-1">
                    <button
                      type="button"
                      onClick={() => moveSectionUp(sectionIndex)}
                      disabled={sectionIndex === 0}
                      className={`p-1 rounded ${
                        sectionIndex === 0
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                      }`}
                      title="Move section up"
                    >
                      <MdKeyboardArrowUp size={20} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveSectionDown(sectionIndex)}
                      disabled={sectionIndex === formik.values.sections.length - 1}
                      className={`p-1 rounded ${
                        sectionIndex === formik.values.sections.length - 1
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                      }`}
                      title="Move section down"
                    >
                      <MdKeyboardArrowDown size={20} />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSection(sectionIndex)}
                    className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                    title="Delete section"
                  >
                    <MdDelete size={20} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 mb-4">
                <FormInput
                  label="Section Title"
                  name={`sections.${sectionIndex}.title`}
                  value={section.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter section title"
                  required={true}
                />
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-900">
                    Section Description
                  </label>
                  <textarea
                    name={`sections.${sectionIndex}.description`}
                    value={section.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter section description"
                    rows="2"
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    required
                  />
                </div>
              </div>

              <div className="space-y-4">
                {section.fields.map((field, fieldIndex) => {
                  const isRequiredField = requiredEnrolTemplateFields.includes(field.name);
                  return (
                    <div
                      key={fieldIndex}
                      className={`grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-lg ${
                        isRequiredField
                          ? "bg-blue-50 border-2 border-blue-200"
                          : "bg-gray-50 border border-gray-200"
                      }`}
                    >
                      {/* Visual indicator for field type */}
                      <div className="md:col-span-2 flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          {isRequiredField ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Required Field
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Custom Field
                            </span>
                          )}
                          <span className="text-xs text-gray-500">#{fieldIndex + 1}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          {/* Field reorder buttons */}
                          <div className="flex items-center space-x-1">
                            <button
                              type="button"
                              onClick={() => moveFieldUp(sectionIndex, fieldIndex)}
                              disabled={fieldIndex === 0}
                              className={`p-1 rounded ${
                                fieldIndex === 0
                                  ? "text-gray-300 cursor-not-allowed"
                                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                              }`}
                              title="Move field up"
                            >
                              <MdKeyboardArrowUp size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveFieldDown(sectionIndex, fieldIndex)}
                              disabled={fieldIndex === section.fields.length - 1}
                              className={`p-1 rounded ${
                                fieldIndex === section.fields.length - 1
                                  ? "text-gray-300 cursor-not-allowed"
                                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                              }`}
                              title="Move field down"
                            >
                              <MdKeyboardArrowDown size={16} />
                            </button>
                          </div>
                          {!isRequiredField && (
                            <button
                              type="button"
                              onClick={() => removeField(sectionIndex, fieldIndex)}
                              className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                              title="Delete field"
                            >
                              <MdDelete size={16} />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Field Label */}
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          Field Label{" "}
                          {(isRequiredField || field.required) && (
                            <span className="text-red-500">*</span>
                          )}
                        </label>
                        {isRequiredField ? (
                          <div className="bg-gray-100 border border-gray-300 text-gray-600 text-sm rounded-lg p-2.5 flex items-center">
                            <span className="flex-1">{field.label}</span>
                            <svg
                              className="w-4 h-4 text-gray-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        ) : (
                          <FormInput
                            name={`sections.${sectionIndex}.fields.${fieldIndex}.label`}
                            value={field.label}
                            onChange={(e) =>
                              handleFieldLabelChange(sectionIndex, fieldIndex, e.target.value)
                            }
                            onBlur={formik.handleBlur}
                            placeholder="Enter field label"
                            required={false}
                          />
                        )}
                      </div>

                      {/* Field Type */}
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          Field Type
                        </label>
                        {isRequiredField ? (
                          <div className="bg-gray-100 border border-gray-300 text-gray-600 text-sm rounded-lg p-2.5 flex items-center">
                            <span className="flex-1">
                              {fieldTypes.find((t) => t.value === field.type)?.label || field.type}
                            </span>
                            <svg
                              className="w-4 h-4 text-gray-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        ) : (
                          <select
                            name={`sections.${sectionIndex}.fields.${fieldIndex}.type`}
                            value={field.type}
                            onChange={(e) =>
                              handleFieldTypeChange(sectionIndex, fieldIndex, e.target.value)
                            }
                            onBlur={formik.handleBlur}
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                            required
                          >
                            {fieldTypes.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      {/* Hidden field name */}
                      <input
                        type="hidden"
                        name={`sections.${sectionIndex}.fields.${fieldIndex}.name`}
                        value={field.name}
                      />
                      {/* Field Options - only for custom fields */}
                      {!isRequiredField && FIELD_TYPES_WITH_OPTIONS.includes(field.type) && (
                        <div className="md:col-span-2">
                          <label className="block mb-2 text-sm font-medium text-gray-900">
                            Field Options (one per line)
                          </label>
                          <textarea
                            name={`sections.${sectionIndex}.fields.${fieldIndex}.options`}
                            value={(field.options || []).join("\n")}
                            onChange={(e) => {
                              const sections = [...formik.values.sections];
                              sections[sectionIndex].fields[fieldIndex].options =
                                e.target.value.split("\n");
                              formik.setFieldValue("sections", sections);
                            }}
                            onBlur={formik.handleBlur}
                            placeholder="Enter options (one per line)"
                            rows="3"
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                            required={FIELD_TYPES_WITH_OPTIONS.includes(field.type)}
                          />
                        </div>
                      )}

                      {/* Required checkbox - only for custom fields */}
                      {!isRequiredField && (
                        <div className="md:col-span-2 flex items-center space-x-4">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              name={`sections.${sectionIndex}.fields.${fieldIndex}.required`}
                              checked={field.required}
                              onChange={formik.handleChange}
                              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                            />
                            <span className="text-sm text-gray-700">Required field</span>
                          </label>
                        </div>
                      )}

                      {/* Required field indicator */}
                      {isRequiredField && (
                        <div className="md:col-span-2">
                          <div className="flex items-center space-x-2 text-sm text-blue-600">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <span>This is a required system field and cannot be modified</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="flex items-center justify-between">
                  <h4 className="text-md font-medium">Fields</h4>
                  <button
                    type="button"
                    onClick={() => addNewField(sectionIndex)}
                    className="inline-flex items-center text-indigo-600 hover:text-indigo-700"
                  >
                    <MdAdd size={20} className="mr-1" />
                    Add Field
                  </button>
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addNewSection}
            className="inline-flex items-center justify-center py-2 px-4 border-2 border-dashed border-gray-300 rounded-lg text-indigo-600 hover:text-indigo-700 hover:border-gray-400"
          >
            <MdAdd size={20} className="mr-2" />
            Add New Section
          </button>
        </div>
        <div className="mt-6 flex justify-end">
          {loading ? (
            <ButtonLoading styles="inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-indigo-400" />
          ) : (
            <button
              type="submit"
              className="inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-md font-medium rounded-full text-white bg-indigo-800 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Update Form Template
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default UpdateEnrollmentForm;
